import React, { useEffect, useRef, useState } from 'react';
import { unifiedTasksApi } from '../../api/unifiedTasks';
import { Button, ProgressBar } from '../../components/ui';
import './lanes.css';

/**
 * "Export tasks" dialog, extracted from the legacy Tasks.tsx (Tasks Rebuild
 * Task 12) -- a self-contained port of its exportParams/exportInProgress/
 * exportProgress/exportDownloadUrl state + handleStartExport/
 * pollExportProgress/handleDownloadExport/resetExportDialog logic and the
 * "Export Dialog" JSX block. Replaces LaneShell's `handleExportStub`
 * `window.alert` placeholder (T11 TODO).
 *
 * Progress polling lives entirely inside this component (self-contained per
 * the Task 12 brief) -- it owns its own `setTimeout` polling loop and clears
 * it on unmount/close so a closed dialog can't keep polling in the
 * background.
 */

type ExportFormat = 'csv' | 'excel';

interface ExportParams {
  taskType: string;
  status: string;
  startDate: string;
  endDate: string;
  format: ExportFormat;
}

const INITIAL_PARAMS: ExportParams = {
  taskType: 'all',
  status: 'all',
  startDate: '',
  endDate: '',
  format: 'csv',
};

export interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  showToast?: (toast: { type: 'success' | 'error' | 'info'; message: string }) => void;
}

export const ExportDialog: React.FC<ExportDialogProps> = ({ isOpen, onClose, showToast }) => {
  const [params, setParams] = useState<ExportParams>(INITIAL_PARAMS);
  const [inProgress, setInProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Item 17: `clearPoll()` only cancels the NEXT scheduled setTimeout re-poll --
  // it can't recall a `getExportProgress`/`startExport` promise that's already
  // in flight. When such a promise resolves after the dialog has been closed (or
  // after `reset()`/a fresh export invalidated the old poll chain), its
  // `.then()`/`.catch()` would otherwise still `setState` on this
  // long-lived component, silently repopulating a dismissed dialog with stale
  // progress/downloadUrl so a reopen flashes a "completed" export the user
  // already walked away from. This ref gates every such callback: `reset()`
  // flips it true (covering handleClose + the unmount effect), and a new export
  // flips it false again so legitimate updates aren't permanently suppressed.
  const ignoreRef = useRef(false);

  const clearPoll = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  // Belt-and-braces: if this dialog's owning lane unmounts (e.g. the staff
  // member switches lanes) while a poll is in flight, stop scheduling more.
  // Unmount: stop polling AND flag in-flight responses as stale so a late
  // resolution can't fire toasts/setState for a dialog that no longer exists.
  useEffect(() => () => { ignoreRef.current = true; clearPoll(); }, []);

  const reset = () => {
    ignoreRef.current = true;
    clearPoll();
    setParams(INITIAL_PARAMS);
    setInProgress(false);
    setProgress(0);
    setDownloadUrl(null);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const pollExportProgress = (id: string) => {
    unifiedTasksApi.getExportProgress(id)
      .then((res) => {
        // Dialog was closed / reset / a new export started while this request
        // was in flight -- drop the stale response instead of bleeding it back
        // into a dismissed dialog (Item 17).
        if (ignoreRef.current) return;
        if (res.progress != null) setProgress(res.progress);
        if (res.status === 'completed' && res.downloadUrl) {
          setDownloadUrl(res.downloadUrl);
          setInProgress(false);
          showToast?.({ type: 'success', message: 'Export completed.' });
        } else if (res.status === 'error') {
          setInProgress(false);
          setError(res.error || 'Export failed');
        } else {
          pollTimerRef.current = setTimeout(() => pollExportProgress(id), 1000);
        }
      })
      .catch(() => {
        if (ignoreRef.current) return;
        setInProgress(false);
        setError('Failed to check export progress');
      });
  };

  const handleStartExport = async () => {
    try {
      // A fresh export re-arms the ignore gate (reset() -- via a prior
      // handleClose -- may have left it true; handleStartExport resets the
      // state fields directly rather than calling reset()).
      ignoreRef.current = false;
      setInProgress(true);
      setProgress(0);
      setDownloadUrl(null);
      setError(null);
      const payload: Parameters<typeof unifiedTasksApi.startExport>[0] = { format: params.format };
      if (params.taskType !== 'all') payload.taskType = params.taskType;
      if (params.status !== 'all') payload.status = params.status;
      if (params.startDate) payload.startDate = params.startDate;
      if (params.endDate) payload.endDate = params.endDate;
      const result = await unifiedTasksApi.startExport(payload);
      // Dialog was closed while startExport itself was in flight -- don't kick
      // off a poll chain for an export the user already dismissed (Item 17).
      if (ignoreRef.current) return;
      pollExportProgress(result.exportId);
    } catch (e) {
      if (ignoreRef.current) return;
      console.error('Error starting export:', e);
      setError('Failed to start export');
      setInProgress(false);
    }
  };

  const handleDownloadExport = async () => {
    if (!downloadUrl) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${downloadUrl}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to download file');
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `tasks_export_${new Date().toISOString().split('T')[0]}.${params.format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showToast?.({ type: 'success', message: 'Download started.' });
    } catch (e) {
      console.error('Error downloading export:', e);
      showToast?.({ type: 'error', message: 'Failed to download export file.' });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="lane-modal-backdrop" onClick={handleClose}>
      <div className="lane-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="lane-modal__title">Export Tasks</h2>

        {error && <div className="lane-modal__error">{error}</div>}

        {!downloadUrl ? (
          <>
            <div className="lane-modal__field">
              <label>Task type:</label>
              <select
                className="lane-modal__input"
                value={params.taskType}
                disabled={inProgress}
                onChange={(e) => setParams({ ...params, taskType: e.target.value })}
              >
                <option value="all">All types</option>
                <option value="email_lender_response">Lender response</option>
                <option value="email_lender_query">Lender query</option>
                <option value="email_client">Client email</option>
                <option value="email_other">Other email</option>
                <option value="manual">Manual tasks</option>
              </select>
            </div>

            <div className="lane-modal__field">
              <label>Status:</label>
              <select
                className="lane-modal__input"
                value={params.status}
                disabled={inProgress}
                onChange={(e) => setParams({ ...params, status: e.target.value })}
              >
                <option value="all">All statuses</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In progress</option>
                <option value="completed">Completed</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <div className="lane-modal__field">
              <label>Date range:</label>
              <div className="export-dialog__date-range">
                <input
                  type="date"
                  className="lane-modal__input"
                  value={params.startDate}
                  disabled={inProgress}
                  onChange={(e) => setParams({ ...params, startDate: e.target.value })}
                />
                <input
                  type="date"
                  className="lane-modal__input"
                  value={params.endDate}
                  disabled={inProgress}
                  onChange={(e) => setParams({ ...params, endDate: e.target.value })}
                />
              </div>
            </div>

            <div className="lane-modal__field">
              <label>Format:</label>
              <div className="export-dialog__format-options">
                <label>
                  <input
                    type="radio"
                    name="export-format"
                    value="csv"
                    checked={params.format === 'csv'}
                    disabled={inProgress}
                    onChange={() => setParams({ ...params, format: 'csv' })}
                  />
                  CSV
                </label>
                <label>
                  <input
                    type="radio"
                    name="export-format"
                    value="excel"
                    checked={params.format === 'excel'}
                    disabled={inProgress}
                    onChange={() => setParams({ ...params, format: 'excel' })}
                  />
                  Excel
                </label>
              </div>
            </div>

            {inProgress && (
              <div className="export-dialog__progress">
                <ProgressBar value={progress} max={100} />
                <p>Exporting... {progress}%</p>
              </div>
            )}
          </>
        ) : (
          <div className="export-dialog__complete">
            <p>Export complete. Your file is ready for download.</p>
            <Button variant="primary" onClick={handleDownloadExport}>
              Download {params.format.toUpperCase()} file
            </Button>
          </div>
        )}

        <div className="lane-modal__actions">
          <Button variant="secondary" onClick={handleClose}>{downloadUrl ? 'Close' : 'Cancel'}</Button>
          {!downloadUrl && !inProgress && (
            <Button variant="primary" onClick={handleStartExport}>Start export</Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExportDialog;
